import { TERMS_LINKS } from '@avalara/bat-apps-shared/constants';
import { isFreeTrialExtensionPending } from '@avalara/bat-apps-shared/helpers/obligations';
import { t } from '@avalara/bat-apps-shared/i18n';
import { SAlert } from '@avalara/skylab-sdk/react';

import type { Connector, TierOffer } from '@avalara/types/index';

import type { TierPlan } from '@avalara/types/index';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import conf from '../../conf';

import analytics from '../../lib/analytics';
import { getConnectorSalesTax } from '../../lib/side-effects/avatax';
import { selectState } from '../../selectors';
import { setSelectedTier } from '../../store/buy-flow';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import Banner from '../banner';
import ExternalLink from '../external-link';
import TierTile from '../shared/product/tier-tile';
import {
  BaseLinkContainer,
  PickerContainer,
  TierIntroContainer,
  TierSelectionPageWrapper,
} from '../styled/components/buy-flow';
import { PageWrapper } from '../styled/skylab/page';
import Cart from './cart';

const getTiers = (connector: Connector, hideConnectorTiers: boolean): Array<TierOffer> => {
  // If user has no supported connector, or removed his connector via the UI, default to API:
  if (!connector.metadata) return [];

  const { paidOffer1, paidOffer2, paidOffer3, paidOffer4 } = connector.metadata;

  // NOTE(Sasha): One-off change for WooCommerce where tiers returned are 2,3,4 per AAA-5796
  if (connector.id === 'a0n3300000FSK2zAAH') {
    return [paidOffer2, paidOffer3, paidOffer4];
  }
  if (hideConnectorTiers) {
    return [paidOffer3, paidOffer4];
  }
  return [paidOffer1, paidOffer2, paidOffer4];
};

const TierSelector = () => {
  const dispatch = useAppDispatch();
  const {
    account: { accountId, connector },
    avataxSignup: { selectedConnector },
    buyFlow: { selectedTier, trialExtension },
    features,
  } = useSelector(selectState);

  const connectorToUse = selectedConnector || connector;
  const tiers = getTiers(connectorToUse, features.HIDELOWERCONNECTORTIER);
  const trialWasExtended = isFreeTrialExtensionPending(accountId || 0);
  const hasNonApiConnector = connectorToUse.id !== 'a0n4000000ChMxlAAF';

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    analytics.page('PURCHASE_START', { connector: selectedConnector?.name.toUpperCase() });
    dispatch(getConnectorSalesTax());
  }, []);

  const onSelectTier = (tier: TierPlan) => dispatch(setSelectedTier(tier));

  return (
    <>
      <Banner
        backgroundImage={`${conf.ASSET_PATH}/assets/images/banner-background.png`}
        image={`${conf.ASSET_PATH}/assets/images/company_w.svg`}
        primaryText='Purchase your subscription'
      />
      <PageWrapper>
        <TierSelectionPageWrapper offsetRight={50}>
          <div>
            <TierIntroContainer>
              <h2 className='pad-bottom-md' data-ref='buy.tier-selector.header'>
                {trialExtension
                  ? 'Your free trial has ended — buy a subscription to keep using AvaTax'
                  : 'Purchase your subscription'}
              </h2>
              {trialWasExtended ? (
                <SAlert data-ref='buy.tier-selector.trial-extended.alert' status='warning'>
                  <div>{t('client.general.your-trial-extension-is-being-provisioned-hang-tight')}</div>
                </SAlert>
              ) : null}
              <div>
                <img
                  className='pad-top-md'
                  data-ref='buy.tier-selector.avatax.image'
                  src={`${conf.ASSET_PATH}/assets/images/AvalaraAvaTax.svg`}
                  alt={t('client.accessibility.avalara-avatax-logo')}
                />
                <p>{t('client.general.avatax-processes-billions-of-tax-transactions-per-year-acros')}</p>
                <div>
                  <p>
                    {t('client.general.included-in-your-subscription-api-access')}
                    {hasNonApiConnector && (
                      <span data-ref='buy.tier-selector.api-access.connector'>{` and ${selectedConnector?.name}`}</span>
                    )}
                  </p>
                </div>
              </div>
            </TierIntroContainer>
            <PickerContainer>
              {tiers.map((t) => (
                <TierTile
                  frequency={'annually'}
                  title={{ emp: t.quantity.toLocaleString('en-US'), end: ' TRANSACTIONS' }}
                  key={`${t.id}-${t.tierNumber}`}
                  onSelectProduct={onSelectTier}
                  selectedProduct={selectedTier}
                  tier={t}
                />
              ))}
              <TierTile
                title={{ end: 'Need more?' }}
                onSelectProduct={onSelectTier}
                selectedProduct={selectedTier}
                isSupportTile={true}
              />
            </PickerContainer>
          </div>
          <Cart />
        </TierSelectionPageWrapper>
        <TierSelectionPageWrapper offsetRight={50}>
          <BaseLinkContainer>
            <div>
              <p>
                <ExternalLink
                  data-ref='buy.tier-selector.transaction-definition.link'
                  href={TERMS_LINKS.avatax}
                  alwaysOpenInNewTab
                >
                  {t('client.general.what-is-a-transaction')}
                </ExternalLink>
              </p>
              <p>
                <ExternalLink
                  data-ref='buy.tier-selector.cancellation-terms.link'
                  href={TERMS_LINKS.general}
                  alwaysOpenInNewTab
                >
                  {t('client.general.see-cancellation-terms')}
                </ExternalLink>
              </p>
            </div>
          </BaseLinkContainer>
        </TierSelectionPageWrapper>
      </PageWrapper>
    </>
  );
};

export default TierSelector;
